import api, { formatApiError } from "@/lib/api";

export function pushUnavailableReason() {
  if (window.self !== window.top) return "Open de app in een apart browsertabblad om pushberichten in te schakelen.";
  if (!window.isSecureContext) return "Pushberichten vereisen een beveiligde HTTPS-verbinding.";
  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  if (ios && !window.matchMedia("(display-mode: standalone)").matches && !navigator.standalone) {
    return "Voeg de app in Safari via Delen toe aan je beginscherm. Open hem daarna vanaf het beginscherm (iOS 16.4 of nieuwer).";
  }
  if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
    return "Deze browser ondersteunt geen pushberichten. Je berichten blijven beschikbaar via het belletje.";
  }
  return "";
}

export async function pushRegistration() {
  await navigator.serviceWorker.register("/sw.js", { scope: "/", updateViaCache: "none" });
  return navigator.serviceWorker.ready;
}

const toBytes = (key) => Uint8Array.from(
  atob(key.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - key.length % 4) % 4)),
  (c) => c.charCodeAt(0)
);

export async function inspectPush() {
  const { data } = await api.get("/push/public-key");
  if (!data.configured) return { enabled: false, configured: false };
  const registration = await pushRegistration();
  const subscription = await registration.pushManager.getSubscription();
  let enabled = false;
  if (subscription) {
    const status = await api.post("/push/status", { endpoint: subscription.endpoint });
    enabled = status.data.enabled && Notification.permission === "granted";
    if (enabled) await api.put("/push/subscription", subscription.toJSON());
    else await disconnectPush();
  }
  return { enabled, configured: true };
}

export async function enablePush() {
  const reason = pushUnavailableReason();
  if (reason) throw new Error(reason);
  // Request permission before any network await, preserving the user gesture on Safari.
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error(permission === "denied"
      ? "Meldingen zijn geblokkeerd. Sta meldingen toe bij de site-instellingen van je browser."
      : "Je hebt nog geen toestemming gegeven. Klik opnieuw op Inschakelen wanneer je zover bent.");
  }
  const { data } = await api.get("/push/public-key");
  if (!data.configured) throw new Error("Pushberichten zijn nog niet ingesteld op de server.");
  const registration = await pushRegistration();
  const existing = await registration.pushManager.getSubscription();
  if (existing) await existing.unsubscribe();
  const subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: toBytes(data.publicKey) });
  try {
    await api.put("/push/subscription", subscription.toJSON());
  } catch (error) {
    await subscription.unsubscribe();
    throw error;
  }
}

export async function disconnectPush() {
  if (!("serviceWorker" in navigator)) return;
  const registration = await navigator.serviceWorker.getRegistration("/");
  const subscription = await registration?.pushManager?.getSubscription();
  if (!subscription) return;
  let serverError;
  try { await api.delete("/push/subscription", { data: { endpoint: subscription.endpoint } }); }
  catch (error) { serverError = error; }
  // Also revoke at the browser service if the API is unreachable during logout.
  const removed = await subscription.unsubscribe();
  const notifications = await registration.getNotifications();
  notifications.forEach((notification) => notification.close());
  if (serverError && !removed) throw serverError;
}

export async function testPush() {
  const registration = await navigator.serviceWorker.getRegistration("/");
  const subscription = await registration?.pushManager.getSubscription();
  if (!subscription) throw new Error("Schakel pushberichten eerst in op dit apparaat.");
  await api.post("/push/test", { endpoint: subscription.endpoint });
}

export function pushError(error) {
  if (error.response) return formatApiError(error.response.data?.detail);
  if (error.name === "AbortError" || error.name === "NotAllowedError") {
    return "De browser kon push niet activeren. Controleer de toestemming voor meldingen en probeer opnieuw in een apart tabblad.";
  }
  return error.message || "Pushberichten konden niet worden ingesteld. Probeer het opnieuw.";
}
